// src/pages/InvoiceHistory.tsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { fetchInvoiceHistory } from '../utils/api';
import { displayPrice, InvoiceSummary } from '../utils/types';
// import useCancelInvoice from '../hooks/useCancelInvoice';
// import useDeleteInvoice from '../hooks/useDeleteInvoice';
import { hasRole } from '../utils/auth';
import { useCustomerContext } from '../context/customerContext';

const MIN_SEARCH_LENGTH = 2; // Define minimum length for search trigger

const InvoiceHistory: React.FC = () => {
    // --- State and Context ---
    const [invoices, setInvoices] = useState<InvoiceSummary[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [status, setStatus] = useState<string>('');
    const [limit, setLimit] = useState<number>(10);
    const [cancellingInvoiceId, setCancellingInvoiceId] = useState<number | null>(null);
    const [deletingInvoiceId, setDeletingInvoiceId] = useState<number | null>(null);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [inputValue, setInputValue] = useState<string>(''); // State to hold raw input value immediately
    const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
    const searchInputRef = useRef<HTMLInputElement>(null); // Ref for the input element

    // --- Hooks ---
    const { customers } = useCustomerContext();
    // const { handleCancelInvoice } = useCancelInvoice();
    // const { handleDeleteInvoice } = useDeleteInvoice();

    // --- User Info ---
    const user = useMemo(() => {
        try {
            const userStr = localStorage.getItem('user');
            if (!userStr || userStr === "undefined") return {};
            return JSON.parse(userStr);
        } catch {
            return {};
        }
    }, []);

    const userId = user.id;

    const role = useMemo(() => {
        try {
            const rolesPermissions = JSON.parse(localStorage.getItem('rolesPermissions') || '{}');
            return rolesPermissions?.roles?.[0];
        } catch {
            return undefined;
        }
    }, []);

    // --- Fetch Invoice History ---
    useEffect(() => {
        if (!userId || !role) {
            setError('User information is missing. Cannot fetch invoice history.');
            setLoading(false);
            return;
        }

        const fetchInvoices = async () => {
            setLoading(true);
            setError(null);
            try {
                // Use searchTerm state for the API call
                const data = await fetchInvoiceHistory(userId, status, currentPage, limit, role, searchTerm);

                // console.log("Fetched invoices:", data.data); // Debugging log

                setInvoices(data.data || []);
                setTotalPages(data.last_page || 1);
            } catch (err: any) {
                if (err.response && err.response.status === 401) {
                    setError('You must log in to view your invoice history.');
                } else {
                    setError(err.message || 'Failed to fetch invoice history');
                }
                setInvoices([]);
                setTotalPages(1);
            } finally {
                setLoading(false);
                // Optional: Refocus input if needed, but usually not required
                // if (document.activeElement !== searchInputRef.current) {
                //   searchInputRef.current?.focus();
                // }
            }
        };

        // Trigger fetch only if searchTerm meets criteria or is cleared
        // This check is implicitly handled by the dependency array and the logic in handleSearchInputChange
        fetchInvoices();

    }, [userId, role, currentPage, status, limit, searchTerm]); // searchTerm triggers the fetch

    // --- Event Handlers ---
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handleStatusChange = (newStatus: string) => {
        setStatus(newStatus);
        setCurrentPage(1);
    };

    const handleLimitChange = (newLimit: number) => {
        setLimit(newLimit);
        setCurrentPage(1);
    };

    // --- Debounced Search Handler ---
    const handleSearchInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const currentValue = event.target.value;
        setInputValue(currentValue); // Update input value immediately for responsiveness

        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        debounceTimeout.current = setTimeout(() => {
            // Check length condition *inside* the timeout
            if (currentValue.length >= MIN_SEARCH_LENGTH) {
                // Only update searchTerm if it's different to avoid unnecessary fetches
                if (currentValue !== searchTerm) {
                    setSearchTerm(currentValue);
                    setCurrentPage(1); // Reset page on new search term
                }
            } else if (searchTerm !== '') {
                // If input is too short, but a search term *was* active, clear the search
                setSearchTerm('');
                setCurrentPage(1); // Reset page when clearing search
            }
            // If input is too short and searchTerm is already empty, do nothing
        }, 500); // 500ms debounce time
    }, [searchTerm]); // Include searchTerm in dependencies

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
        };
    }, []);


    // --- State Update Callbacks for Hooks ---
    const updateInvoiceStatus = (updatedInvoice: InvoiceSummary) => {
        setInvoices(prevInvoices =>
            prevInvoices.map(invoice =>
                invoice.id === updatedInvoice.id ? { ...invoice, status: updatedInvoice.status } : invoice
            )
        );
    };

    const removeDeletedInvoice = (orderId: number) => {
        setInvoices(prevInvoices => prevInvoices.filter(invoice => invoice.id !== orderId));
    };

    // --- Render Logic ---
    // Initial loading state
    if (loading && invoices.length === 0 && !error) {
        return <div className="container mx-auto p-4 text-center">Loading invoice history...</div>;
    }

    // Error state
    if (error) {
        return <div className="container mx-auto p-4 text-red-500 text-center">Error: {error}</div>;
    }

    // console.log("Invoices:", invoices);
    // console.log("customers:", customers);

    return (
        <div className="container mx-auto p-4">
            {hasRole(['super_admin', 'staff', 'technician']) && (
                <div className="mb-4">
                    <Link to="/" className="text-white bg-green-600 hover:bg-green-700 px-4 py-2 rounded-lg text-sm font-medium transition duration-150">
                        Management Screen
                    </Link>
                </div>
            )}

            <h1 className="text-2xl font-bold mb-6">Invoice History</h1>

            {/* Search and Filters */}
            <div className="flex flex-col md:flex-row gap-4 mb-6 items-center">
                {/* Search Input */}
                <div className="w-full md:w-1/2 lg:w-1/3">
                    <label htmlFor="searchFilter" className="sr-only">Search Invoices</label>
                    <input
                        ref={searchInputRef} // Assign ref
                        id="searchFilter"
                        type="search"
                        placeholder={`Search (min ${MIN_SEARCH_LENGTH} chars)...`}
                        value={inputValue} // Bind to immediate input value state
                        onChange={handleSearchInputChange} // Use the debounced handler
                        className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 search-cancel:appearance-none"
                    />
                </div>

                {/* Status Filter */}
                <div className="w-full md:w-auto">
                    <label htmlFor="statusFilter" className="mr-2 text-sm font-medium text-gray-700">Status:</label>
                    <select
                        id="statusFilter"
                        value={status}
                        onChange={(e) => handleStatusChange(e.target.value)}
                        className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                        <option value="">All</option>
                        <option value="pending">Pending</option>
                        <option value="processing">Processing</option>
                        <option value="completed">Completed</option>
                        <option value="cancel">Cancelled</option>
                    </select>
                </div>

                {/* Limit Filter */}
                <div className="w-full md:w-auto">
                    <label htmlFor="limitFilter" className="mr-2 text-sm font-medium text-gray-700">Per page:</label>
                    <select
                        id="limitFilter"
                        value={limit}
                        onChange={(e) => handleLimitChange(parseInt(e.target.value))}
                        className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                        <option value="10">10</option>
                        <option value="20">20</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                    </select>
                </div>
            </div>

            {/* Loading Indicator (Subtle) - Show only when loading is true */}
            {loading && <div className="text-center text-gray-500 py-4">Updating list...</div>}

            {/* Pagination Controls (Consider moving above or below list based on preference) */}
            {!loading && totalPages > 1 && (
                <div className="mb-4 flex justify-center items-center space-x-2"> {/* Moved above list */}
                    <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1 || loading}
                        className="px-4 py-2 border rounded bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition duration-150"
                    >
                        Previous
                    </button>
                    <span className="text-sm text-gray-700">
                        Page {currentPage} of {totalPages}
                    </span>
                    <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages || loading}
                        className="px-4 py-2 border rounded bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition duration-150"
                    >
                        Next
                    </button>
                </div>
            )}

            {/* Invoice List */}
            {/* Show message only when not loading and list is empty */}
            {!loading && invoices.length === 0 ? (
                <p className="text-center text-gray-500 mt-8">
                    {searchTerm || status ? 'No invoices found matching your criteria.' : 'No invoices found.'}
                </p>
            ) : (
                <div className="space-y-4 pb-16">
                    {invoices.map((invoice) => (
                        <div key={invoice.id} className="border border-gray-200 rounded-lg p-4 shadow-sm bg-white hover:shadow-md transition-shadow duration-150">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                                <div>
                                    <p><span className="font-semibold text-gray-600">Invoice ID:</span> {invoice.id}</p>
                                    <p>
                                        {(() => {
                                            const customerObj = customers?.find(c => String(c.id) === String(invoice.customer_id));
                                            return (
                                                <>
                                                    <span className="font-semibold text-gray-600">Customer:</span> {invoice.customer_id} : {
                                                        customerObj?.first_name + ' ' + customerObj?.last_name || customerObj?.name || customerObj?.email || 'Error?'
                                                    }
                                                </>
                                            );
                                        })()}
                                    </p>
                                </div>
                                <div>
                                    <p><span className="font-semibold text-gray-600">Date:</span> {new Date(invoice.created_at).toLocaleString()}</p>
                                    <p>
                                        <span className="font-semibold text-gray-600">Status:</span>
                                        <span className={`ml-1 font-medium
                                            ${invoice.status === 'pending' ? 'text-blue-600'
                                                : invoice.status === 'processing' ? 'text-yellow-600'
                                                    : invoice.status === 'completed' ? 'text-green-600'
                                                        : invoice.status === 'cancel' ? 'text-red-600'
                                                            : 'text-gray-700'
                                            }`}>
                                            {invoice.status}
                                        </span>
                                    </p>
                                </div>
                                <div className="md:text-right">
                                    <p className='font-semibold text-lg text-green-700'>Total: {displayPrice(Number(invoice.balance))}</p>
                                    <p className="font-semibold text-sm text-gray-700">
                                        Total Items: {Array.isArray(invoice.items) ? invoice.items.length : 0}
                                    </p>
                                </div>
                            </div>
                            {/* Action Buttons */}
                            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-100">
                                <Link to={`/invoices/${invoice.id}`} className="bg-blue-500 hover:bg-blue-600 text-xs text-white font-bold py-1 px-3 rounded transition duration-150">
                                    View Details
                                </Link>
                                {/* 
                                {invoice.status !== 'cancel' && (invoice.user_id === userId || hasRole(['super_admin', 'staff', 'technician'])) && (
                                    <button
                                        onClick={() => {
                                            setCancellingInvoiceId(invoice.id);
                                            handleCancelInvoice(invoice.id, updateInvoiceStatus)
                                                .catch(err => console.error("Failed to cancel invoice:", err))
                                                .finally(() => setCancellingInvoiceId(null));
                                        }}
                                        className={`bg-red-500 hover:bg-red-600 text-xs text-white font-bold py-1 px-3 rounded transition duration-150 ${cancellingInvoiceId === invoice.id || cancellingInvoiceId !== null ? 'opacity-50 cursor-not-allowed' : ''}`}
                                        disabled={cancellingInvoiceId !== null}
                                    >
                                        {cancellingInvoiceId === invoice.id ? 'Cancelling...' : 'Cancel'}
                                    </button>
                                )}

                                {invoice.status === 'cancel' && hasRole(['super_admin', 'staff', 'technician']) && (
                                    <button
                                        onClick={async () => {
                                            setDeletingInvoiceId(invoice.id);
                                            try {
                                                await handleDeleteInvoice(invoice.id, removeDeletedInvoice);
                                            } catch (err) {
                                                console.error("Failed to delete invoice:", err);
                                            } finally {
                                                setDeletingInvoiceId(null);
                                            }
                                        }}
                                        className={`bg-orange-300 hover:bg-orange-400 text-xs text-red-700 font-bold py-1 px-3 rounded transition duration-150 ${deletingInvoiceId === invoice.id || deletingInvoiceId !== null ? 'opacity-50 cursor-not-allowed' : ''}`}
                                        disabled={deletingInvoiceId !== null}
                                    >
                                        {deletingInvoiceId === invoice.id ? 'Deleting...' : 'Delete'}
                                    </button>
                                )} */}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Pagination Controls (Consider moving above or below list based on preference) */}
            {!loading && totalPages > 1 && (
                <div className="mb-4 flex justify-center items-center space-x-2"> {/* Moved above list */}
                    <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1 || loading}
                        className="px-4 py-2 border rounded bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition duration-150"
                    >
                        Previous
                    </button>
                    <span className="text-sm text-gray-700">
                        Page {currentPage} of {totalPages}
                    </span>
                    <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages || loading}
                        className="px-4 py-2 border rounded bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition duration-150"
                    >
                        Next
                    </button>
                </div>
            )}

        </div>
    );
};

export default InvoiceHistory;
